import * as React from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { listAllTickets, type SupportTicket } from "@/lib/support.functions";

export const Route = createFileRoute("/_authed/admin/support")({
  head: () => ({
    meta: [
      { title: "Admin: Support: CapitalDart" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: AdminSupportPage,
});

const STATUSES = ["all", "open", "in_progress", "resolved", "closed"] as const;
const CATEGORIES = [
  "all",
  "bug",
  "question",
  "billing",
  "feature",
  "account",
  "other",
] as const;

function statusBadgeClass(s: SupportTicket["status"]): string {
  switch (s) {
    case "open":
      return "bg-amber-100 text-amber-900 border-amber-200";
    case "in_progress":
      return "bg-blue-100 text-blue-900 border-blue-200";
    case "resolved":
      return "bg-emerald-100 text-emerald-900 border-emerald-200";
    case "closed":
      return "bg-zinc-100 text-zinc-700 border-zinc-200";
  }
}
function fmt(iso: string): string {
  return new Date(iso).toLocaleString();
}

function AdminSupportPage() {
  const listFn = useServerFn(listAllTickets);
  const [statusFilter, setStatusFilter] = React.useState<string>("all");
  const [categoryFilter, setCategoryFilter] = React.useState<string>("all");
  const [unreadOnly, setUnreadOnly] = React.useState(false);
  const [dateFrom, setDateFrom] = React.useState("");
  const [dateTo, setDateTo] = React.useState("");

  const { data: tickets, isLoading } = useQuery({
    queryKey: [
      "admin-support-tickets",
      statusFilter,
      categoryFilter,
      unreadOnly,
      dateFrom,
      dateTo,
    ],
    queryFn: () =>
      listFn({
        data: {
          statusFilter: statusFilter === "all" ? null : (statusFilter as never),
          categoryFilter:
            categoryFilter === "all" ? null : (categoryFilter as never),
          hasUnreadAdminReply: unreadOnly ? true : null,
          dateFrom: dateFrom ? new Date(dateFrom).toISOString() : null,
          dateTo: dateTo ? new Date(dateTo).toISOString() : null,
        },
      }),
  });

  return (
    <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-10">
      <h1 className="mb-4 font-serif text-2xl text-brand-ink">Support tickets</h1>

      <div className="mb-4 flex flex-wrap items-end gap-3 rounded-lg border border-brand-line bg-brand-paper p-3">
        <div>
          <label className="block text-xs uppercase text-brand-muted">Status</label>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
            <SelectContent>
              {STATUSES.map((s) => (
                <SelectItem key={s} value={s} className="capitalize">{s.replace("_", " ")}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="block text-xs uppercase text-brand-muted">Category</label>
          <Select value={categoryFilter} onValueChange={setCategoryFilter}>
            <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
            <SelectContent>
              {CATEGORIES.map((c) => (
                <SelectItem key={c} value={c} className="capitalize">{c}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="block text-xs uppercase text-brand-muted">From</label>
          <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="w-40" />
        </div>
        <div>
          <label className="block text-xs uppercase text-brand-muted">To</label>
          <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="w-40" />
        </div>
        <div className="flex items-center gap-2 pb-1">
          <Switch checked={unreadOnly} onCheckedChange={setUnreadOnly} id="unread" />
          <label htmlFor="unread" className="text-xs uppercase text-brand-muted">
            Awaiting user reply
          </label>
        </div>
      </div>

      <div className="overflow-hidden rounded-lg border border-brand-line">
        <table className="w-full text-sm">
          <thead className="bg-brand-paper-2 text-left text-xs uppercase tracking-wider text-brand-muted">
            <tr>
              <th className="px-3 py-2">Created</th>
              <th className="px-3 py-2">User</th>
              <th className="px-3 py-2">Subject</th>
              <th className="px-3 py-2">Category</th>
              <th className="px-3 py-2">Status</th>
              <th className="px-3 py-2">Last activity</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr><td colSpan={6} className="px-3 py-8 text-center text-brand-muted">Loading…</td></tr>
            ) : !tickets || tickets.length === 0 ? (
              <tr><td colSpan={6} className="px-3 py-8 text-center text-brand-muted">No tickets match.</td></tr>
            ) : (
              tickets.map((t) => (
                <tr
                  key={t.id}
                  className="border-t border-brand-line hover:bg-brand-paper-2"
                >
                  <td>
                    <Link to="/admin/support/$ticketId" params={{ ticketId: t.id }} className="block cursor-pointer px-3 py-2 text-brand-muted no-underline">
                      {fmt(t.created_at)}
                    </Link>
                  </td>
                  <td>
                    <Link to="/admin/support/$ticketId" params={{ ticketId: t.id }} className="block cursor-pointer px-3 py-2 text-brand-ink no-underline">
                      {t.user_id ? t.user_email : <span className="italic text-brand-muted">anonymous</span>}
                    </Link>
                  </td>
                  <td>
                    <Link
                      to="/admin/support/$ticketId"
                      params={{ ticketId: t.id }}
                      className="block cursor-pointer px-3 py-2 text-brand-ink underline decoration-brand-line underline-offset-4 hover:decoration-brand-ink"
                    >
                      {t.subject}
                    </Link>
                  </td>
                  <td>
                    <Link to="/admin/support/$ticketId" params={{ ticketId: t.id }} className="block cursor-pointer px-3 py-2 capitalize text-brand-ink/80 no-underline">
                      {t.category ?? "—"}
                    </Link>
                  </td>
                  <td>
                    <Link to="/admin/support/$ticketId" params={{ ticketId: t.id }} className="block cursor-pointer px-3 py-2 no-underline">
                      <Badge variant="outline" className={statusBadgeClass(t.status)}>
                        {t.status.replace("_", " ")}
                      </Badge>
                    </Link>
                  </td>
                  <td>
                    <Link to="/admin/support/$ticketId" params={{ ticketId: t.id }} className="block cursor-pointer px-3 py-2 text-brand-muted no-underline">
                      {fmt(t.last_message_at)}
                    </Link>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}